import React, { useState, useEffect } from 'react';
import RedditSimulator from '../utils/redditSimulator';
import SimpleFormatter from '../utils/simpleFormatter';
import PDFExportService from '../utils/pdfExportService';

const RedditInsights = ({ selectedSegments, confirmedMarket, onAnalysisComplete }) => {
  const [isSearching, setIsSearching] = useState(false);
  const [searchProgress, setSearchProgress] = useState(0);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const redditSimulator = new RedditSimulator();
  const pdfExporter = new PDFExportService();

  // Auto-start the search when component mounts
  useEffect(() => {
    startRedditSearch();
  }, []);


  // Start Reddit conversation simulation
  const startRedditSearch = async () => {
    setIsSearching(true);
    setError(null);
    setSearchProgress(0);
    setAnalysis(null);

    try {
      if (selectedSegments.length === 0) {
        throw new Error('No segments selected. Please select market segments to analyze.');
      }

      console.log('🎭 Generating realistic Reddit conversations...');

      // Step 1: Generate conversations
      setSearchProgress(20);
      const conversations = await redditSimulator.generateConversations(selectedSegments, confirmedMarket);
      setSearchProgress(60);

      console.log(`📊 Generated ${conversations.length} realistic Reddit conversations`);

      // Step 2: Analyze conversations
      setSearchProgress(80);
      const analysisResults = redditSimulator.analyzeConversations(conversations);

      setSearchProgress(100);
      setAnalysis(analysisResults);

      console.log('Analysis set:', analysisResults); // Debug log

      // Callback to parent component
      if (onAnalysisComplete) {
        onAnalysisComplete(analysisResults);
      }

    } catch (err) {
      console.error('❌ Reddit simulation failed:', err);
      setError(err.message);
    } finally {
      setIsSearching(false);
    }
  };

  // Download insights report as PDF
  const downloadPDFReport = async () => {
    if (!analysis) return;

    try {
      const { download } = await pdfExporter.generateRedditInsightsPDF(analysis, confirmedMarket);
      download();
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF report. Please try again.');
    }
  };

  // Download insights report as Markdown (backup option)
  const downloadMarkdownReport = () => {
    if (!analysis) return;

    const report = `
# Reddit Conversation Insights Report

## Executive Summary
${analysis.summary}

## Key Pain Points Identified
${analysis.keyPainPoints}

## User Demographics & Profiles
${analysis.demographics}

## Market Opportunities
${analysis.opportunities}

## Notable User Quotes
${analysis.notableQuotes}

---
*Analysis based on simulated Reddit conversations.*
`;

    const blob = new Blob([report], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reddit-insights-${Date.now()}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 mb-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div className="mb-4 md:mb-0">
          <h3 className="text-2xl font-bold text-gray-900 mb-3">🔍 Real User Insights from Reddit</h3>
          <p className="text-lg text-gray-600 max-w-2xl">
            Discover what real people are saying about these pain points on Reddit
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          {analysis && (
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
            >
              {showDetails ? 'Hide Details' : 'Show Details'}
            </button>
          )}
        </div>
      </div>

      {/* Search Button */}
      {!isSearching && !analysis && (
        <div className="text-center py-12">
          <div className="mb-8">
            <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl">🔍</span>
            </div>
            <h4 className="text-xl font-semibold text-gray-800 mb-3">
              Ready to Discover Real User Insights?
            </h4>
            <p className="text-gray-600 max-w-md mx-auto mb-8">
              Search Reddit to find authentic conversations about your selected pain points and get AI-powered analysis
            </p>
          </div>
          <button
            onClick={startRedditSearch}
            className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold py-4 px-8 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1 text-lg"
          >
            🚀 Search Reddit for Real Conversations
          </button>
          <p className="text-sm text-gray-500 mt-6 max-w-md mx-auto">
            This will search Reddit for discussions about the selected pain points and analyze them with AI
          </p>
        </div>
      )}

      {/* Searching Progress */}
      {isSearching && (
        <div className="space-y-6 py-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-lg font-semibold text-gray-800">Searching Reddit...</span>
            <span className="text-lg font-medium text-orange-600">{searchProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
            <div
              className="bg-gradient-to-r from-orange-500 to-red-600 h-4 rounded-full transition-all duration-500 ease-out shadow-sm"
              style={{ width: `${searchProgress}%` }}
            ></div>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center space-x-3 px-6 py-3 bg-orange-50 rounded-lg">
              <span className="text-lg">
                {searchProgress < 30 && "🔍"}
                {searchProgress >= 30 && searchProgress < 60 && "📡"}
                {searchProgress >= 60 && searchProgress < 80 && "🤖"}
                {searchProgress >= 80 && "📊"}
              </span>
              <span className="text-gray-700 font-medium">
                {searchProgress < 30 && "Generating search queries..."}
                {searchProgress >= 30 && searchProgress < 60 && "Searching Reddit conversations..."}
                {searchProgress >= 60 && searchProgress < 80 && "Analyzing conversations with AI..."}
                {searchProgress >= 80 && "Generating insights..."}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="text-red-800 font-medium">Search Failed</h4>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Analysis Results */}
      {analysis && (
        <div className="space-y-8">
          {/* Success Header */}
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">✅</span>
            </div>
            <h4 className="text-2xl font-bold text-gray-900 mb-2">Analysis Complete!</h4>
            <p className="text-gray-600">We've analyzed real Reddit conversations to uncover valuable insights</p>
            <p className="text-xs text-gray-400 mt-2">Debug: Analysis object exists: {analysis ? 'Yes' : 'No'}</p>

            {/* Export Buttons */}
            <div className="flex justify-center gap-4 mt-6">
              <button
                onClick={downloadPDFReport}
                className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
              >
                <span className="mr-2">📄</span>
                Download PDF Report
              </button>
              <button
                onClick={downloadMarkdownReport}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg flex items-center"
              >
                <span className="mr-2">📝</span>
                Download Markdown
              </button>
            </div>
          </div>

          {/* Summary Card */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-8 shadow-sm">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                <span className="text-xl">📋</span>
              </div>
              <h4 className="text-xl font-bold text-blue-900">Key Findings</h4>
            </div>
            <div
              className="text-gray-800 leading-relaxed text-base"
              dangerouslySetInnerHTML={{
                __html: SimpleFormatter.formatAsBullets(analysis.summary)
              }}
            />
          </div>

          {/* Detailed Analysis */}
          {showDetails && (
            <div className="space-y-6">
              {/* Pain Points */}
              {analysis.keyPainPoints && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-6 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-lg">😣</span>
                    </div>
                    <h4 className="text-lg font-bold text-red-900">Pain Points Identified</h4>
                  </div>
                  <div
                    className="text-gray-800 leading-relaxed text-base"
                    dangerouslySetInnerHTML={{
                      __html: SimpleFormatter.formatAsList(analysis.keyPainPoints)
                    }}
                  />
                </div>
              )}

              {/* Demographics */}
              {analysis.demographics && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-6 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-lg">👥</span>
                    </div>
                    <h4 className="text-lg font-bold text-green-900">User Demographics</h4>
                  </div>
                  <div
                    className="text-gray-800 leading-relaxed text-base"
                    dangerouslySetInnerHTML={{
                      __html: SimpleFormatter.formatAsText(analysis.demographics)
                    }}
                  />
                </div>
              )}

              {/* Opportunities */}
              {analysis.opportunities && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-lg">💡</span>
                    </div>
                    <h4 className="text-lg font-bold text-yellow-900">Market Opportunities</h4>
                  </div>
                  <div
                    className="text-gray-800 leading-relaxed text-base"
                    dangerouslySetInnerHTML={{
                      __html: SimpleFormatter.formatAsList(analysis.opportunities)
                    }}
                  />
                </div>
              )}

              {/* Notable Quotes */}
              {analysis.notableQuotes && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-lg">💬</span>
                    </div>
                    <h4 className="text-lg font-bold text-purple-900">What Real Users Are Saying</h4>
                  </div>
                  <div
                    className="text-gray-800 leading-relaxed text-base"
                    dangerouslySetInnerHTML={{
                      __html: SimpleFormatter.formatAsText(analysis.notableQuotes)
                    }}
                  />
                </div>
              )}

              {/* Sample Conversations */}
              {analysis.rawConversations && analysis.rawConversations.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 shadow-sm">
                  <div className="flex items-center mb-6">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-lg">🔗</span>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900">Sample Conversations</h4>
                  </div>
                  <div className="space-y-4">
                    {analysis.rawConversations.slice(0, 5).map((post, index) => (
                      <div key={index} className="bg-white rounded-lg p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                            r/{post.subreddit}
                          </span>
                          <span className="text-sm text-gray-500">
                            💬 {post.numComments} comments • 👍 {post.score} upvotes
                          </span>
                        </div>
                        <h5 className="font-semibold text-gray-900 mb-3 text-base leading-tight">
                          {post.title}
                        </h5>
                        <a
                          href={post.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                        >
                          View Full Conversation
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RedditInsights;